import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar, OptionsObject } from 'notistack';
import { t } from 'i18next';
import { TRIMBOX_REDIRECT_KEY } from '../../constants';
import {
  CurrencyConfig,
  PromoCode,
  PlanTypeForCheckout,
} from '../../features/offering/offeringSlice.types';
import AnalyticsService from '../../services/analytics/analyticsService';
import { EventName } from '../../services/analytics/providers/analyticsProvider';
import { SCOPE_TYPE } from '../../services/credentialFactory';
import MailboxConnector from '../../services/mailboxConnector';
import { openCheckoutPage } from '../../utilities/paywallHelper';

type UsePricingPageActionsProps = {
  userEmail: string | undefined;
  discountPercentage: number;
  includeLifetime: boolean;
  promoCode: PromoCode | undefined;
  returnUrl: string;
  source: 'web' | 'extension' | 'mobile';
  aboveMedium: boolean;
  trialPeriodDays?: number;
};

export function usePricingPageActions({
  userEmail,
  discountPercentage,
  includeLifetime,
  promoCode,
  returnUrl,
  source,
  aboveMedium,
  trialPeriodDays,
}: UsePricingPageActionsProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  function showErrorToast(e: unknown) {
    const snackbarOptions: OptionsObject = {
      variant: 'error',
      preventDuplicate: true,
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
      persist: false,
    };

    const errorMessage = t('failedToSignIn');

    enqueueSnackbar(errorMessage, snackbarOptions);
  }

  const onPlanCheckout = useCallback(
    async (
      plan: PlanTypeForCheckout,
      currency: CurrencyConfig,
      promoCode: PromoCode | undefined
    ) => {
      if (plan.amountWithDiscount == 0) {
        navigate('/');
        return;
      }

      AnalyticsService.track(EventName.PRICING_PAGE_PLAN_SELECT, {
        planInterval: plan.interval,
        planAmount: plan.amountWithoutDiscount,
        includeFree: aboveMedium,
        discountPercentage,
        includeLifetime,
        hasEmail: !!userEmail,
      });

      if (!userEmail) {
        AnalyticsService.track(EventName.CLICK_LOGIN);
        AnalyticsService.track(EventName.LOGIN_START);

        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          const g = google;

          localStorage.setItem(TRIMBOX_REDIRECT_KEY, window.location.href);

          MailboxConnector.create(g).startConnection(SCOPE_TYPE.USER_INFO);
        } catch (e: unknown) {
          AnalyticsService.trackError(EventName.LOGIN_FAILED, e);
          showErrorToast(e);
        }
        return;
      }

      try {
        await openCheckoutPage({
          mailboxId: userEmail,
          selectedPlan: plan.planType,
          currency,
          promoCode: promoCode?.id,
          returnUrl,
          source,
          trialPeriodDays,
        });
      } catch (e) {
        enqueueSnackbar(t('somethingWentWrong'), {
          variant: 'error',
          preventDuplicate: true,
          anchorOrigin: { horizontal: 'center', vertical: 'top' },
          persist: false,
        });
        AnalyticsService.trackError(EventName.CHECKOUT_FAILED, e);
      }
    },
    [
      userEmail,
      discountPercentage,
      includeLifetime,
      promoCode,
      returnUrl,
      source,
      aboveMedium,
      navigate,
      enqueueSnackbar,
    ]
  );

  const onDecline = useCallback(() => {
    AnalyticsService.track(EventName.UPGRADE_PAGE_DECLINE);
    navigate('/');
  }, [navigate]);

  return {
    onPlanCheckout,
    onDecline,
  };
}
